<template>
  <label
    class="relative grid b b-grey-40 rounded-xs bg-white px-3 py-1 focus-within:b-grey-10 [&>*:first-child]:focus-within:c-grey-10"
    :class="[
      { 'bg-grey-90 b-grey-50 placeholder-grey-30': disabled },
      invalid && (variant === 'inverse' ? 'b-red-60' : 'b-red-30'),
    ]"
    data-test-id="vf-textarea"
  >
    <span
      v-if="$slots.default"
      class="form-label"
      :class="disabled ? 'c-grey-30' : invalid ? 'c-red-30' : 'c-grey-20'"
      data-test-id="vf-textarea-label"
    >
      <!-- @slot Slot is meant for `label` only -->
      <slot />
      <span v-if="required" aria-hidden="true"> *</span>
    </span>
    <slot name="textarea">
      <base-textarea
        v-bind="{ ...propsTextarea, disabled, required }"
        class="bg-transparent c-grey-10 placeholder-grey-20"
        :class="{
          'pr-10': isIconShown || $slots.end,
          'pr-18': isIconShown && $slots.end,
          'resize-y': resizable,
        }"
        :model-value="modelValue"
        style="min-height: 4.5rem"
        @update:model-value="modelValue = $event"
      />
    </slot>
    <span
      class="absolute right-0 top-0 mr-3 mt-3 flex gap-1"
      data-test-id="vf-textarea-info"
      @click.prevent
    >
      <!-- With click.prevent input gets focused instead of tooltip displaying when user clicks the icon -->
      <slot name="end" />
      <vf-icon
        v-if="isIconShown"
        :class="invalid ? 'c-red-30' : 'c-green-30'"
        :name="invalid ? 'error' : 'success'"
        size="md"
      />
    </span>
  </label>
</template>

<script lang="ts" setup>
import type { TextareaHTMLAttributes } from 'vue'

const {
  /**
   * Needs to be explicitly set to `undefined` for indeterminate validation state
   */
  invalid = undefined
} = defineProps<{
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines validation state
   * There are 3 possible states
   * Indeterminate === undefined (no validation icons visible)
   * Valid === true
   * Invalid === false
   */
  invalid?: boolean
  /**
   * Allows to pass properties to textarea native element
   */
  propsTextarea?: TextareaHTMLAttributes
  /**
   * Defines whether textarea can be resized
   */
  resizable?: boolean
  /**
   * Defines if the textarea is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
}>()

const modelValue = defineModel<string>()

const { showErrorIcon, showSuccessIcon } = useAppConfig().components.vf.input

const isIconShown = computed(() => {
  if (showErrorIcon && showSuccessIcon) return invalid !== undefined
  if (showErrorIcon) return invalid === true
  if (showSuccessIcon) return invalid === false
})
</script>
